import { useEffect, useRef, useState } from 'react';
import { DialogTitle } from '@headlessui/react';
import { useSetAtom } from 'jotai';
import {
  ExclamationTriangleIcon,
  LockClosedIcon,
} from '@heroicons/react/20/solid';
import { Modal } from '@tailwindComponents/Modal';
import { Button } from '@tailwindComponents/Button';
import { Input } from '@tailwindComponents/Input';
import { Select } from '@tailwindComponents/Select';
import { useAuthHttp } from '@hooks';
import {
  createEbayConsentState,
  readEbayConsentState,
  rememberEbayConsentState,
} from '@utils';
import { WarehouseMatchList } from '@views/WarehouseDetails/types';
import {
  CONTRACTED_MATCH_LIST,
  CREATE_OAUTH_CONSENT_LINK_URL,
} from '../../constants';
import {
  errorResponseStatusCodeAtom,
  responseErrorMessagesAtom,
} from '../../store/jotai';
import { IntegrationConfig } from './integrationsConfig';
import {
  firstValidationMessage,
  isEbay,
  isEbaySignInUrl,
  isServerError,
  warehousePickerHint,
} from './integrationsHelpers';

interface ConnectIntegrationModalProps {
  config: IntegrationConfig | null;
  isOpen: boolean;
  isReconnecting?: boolean;
  setIsOpen: (value: boolean) => void;
  isSubmitting: boolean;
  submitError?: string;
  onSubmit: (values: Record<string, string>) => Promise<void>;
}

interface WarehouseOption {
  label: string;
  value: string;
}

interface OAuthConsentLink {
  consentUrl?: string;
}

const EBAY_SIGN_IN_COULD_NOT_OPEN =
  'Could not open the eBay sign-in. Please try again.';
const EBAY_IS_UNAVAILABLE = "eBay isn't available right now.";
const BROWSER_BLOCKS_SITE_STORAGE =
  'Your browser is blocking this site from storing data, which connecting eBay needs. Allow cookies and site data for Flox, then try again.';

function whyTheEbaySignInCouldNotOpen(error: unknown): string {
  const validationMessage = firstValidationMessage(error);
  if (validationMessage) return validationMessage;
  return isServerError(error)
    ? EBAY_IS_UNAVAILABLE
    : EBAY_SIGN_IN_COULD_NOT_OPEN;
}

function ButtonSpinner() {
  return (
    <svg
      className='h-4 w-4 animate-spin'
      viewBox='0 0 24 24'
      fill='none'
      aria-hidden='true'
    >
      <circle
        className='opacity-25'
        cx='12'
        cy='12'
        r='10'
        stroke='currentColor'
        strokeWidth='4'
      />
      <path
        className='opacity-75'
        fill='currentColor'
        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
      />
    </svg>
  );
}

export function ConnectIntegrationModal({
  config,
  isOpen,
  isReconnecting = false,
  setIsOpen,
  isSubmitting,
  submitError,
  onSubmit,
}: ConnectIntegrationModalProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [logoError, setLogoError] = useState(false);
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>(
    []
  );
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(false);
  const [hasWarehouseLoadFailed, setHasWarehouseLoadFailed] = useState(false);
  const [contractedMatchCount, setContractedMatchCount] = useState(0);
  const [isOpeningEbay, setIsOpeningEbay] = useState(false);
  const [ebaySignInError, setEbaySignInError] = useState('');
  const currentEbaySignIn = useRef<symbol | null>(null);
  const setErrorResponseStatusCode = useSetAtom(errorResponseStatusCodeAtom);
  const setResponseErrorMessages = useSetAtom(responseErrorMessagesAtom);
  const authHttp = useAuthHttp();

  const needsWarehousePicker = Boolean(
    config?.fields.some((field) => field.type === 'warehouse')
  );

  useEffect(() => {
    setValues({});
    setLogoError(false);
    setIsOpeningEbay(false);
    setEbaySignInError('');
    return () => {
      currentEbaySignIn.current = null;
    };
  }, [isOpen]);

  useEffect(() => {
    let hasUnmounted = false;

    const loadContractedWarehouses = async () => {
      setIsLoadingWarehouses(true);
      setHasWarehouseLoadFailed(false);
      setContractedMatchCount(0);
      try {
        const { data } = await authHttp.post<WarehouseMatchList>(
          CONTRACTED_MATCH_LIST,
          {}
        );
        if (!hasUnmounted) {
          setWarehouseOptions(
            (data?.matches ?? []).map((contracted) => ({
              label: contracted.warehouse.name,
              value: contracted.warehouse.warehouseId,
            }))
          );
          setContractedMatchCount(data?.totalCount ?? 0);
        }
      } catch {
        if (!hasUnmounted) {
          setWarehouseOptions([]);
          setHasWarehouseLoadFailed(true);
        }
      } finally {
        if (!hasUnmounted) setIsLoadingWarehouses(false);
      }
    };

    if (isOpen && needsWarehousePicker) {
      loadContractedWarehouses();
    } else {
      setHasWarehouseLoadFailed(false);
      setContractedMatchCount(0);
    }

    return () => {
      hasUnmounted = true;
    };
  }, [isOpen, needsWarehousePicker]);

  if (!config) return null;

  const isEbaySignIn = isEbay(config);
  const errorMessage = isEbaySignIn ? ebaySignInError : submitError;

  const isComplete = config.fields.every(
    (field) => field.optional || (values[field.key] ?? '').trim().length > 0
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isComplete || isSubmitting) return;
    await onSubmit(values);
  };

  const stopOpeningEbay = (reason: string) => {
    setEbaySignInError(reason);
    setIsOpeningEbay(false);
  };

  const handleContinueToEbay = async () => {
    const thisEbaySignIn = Symbol('eBay sign-in');
    currentEbaySignIn.current = thisEbaySignIn;
    const hasBeenAbandoned = () => currentEbaySignIn.current !== thisEbaySignIn;
    setIsOpeningEbay(true);
    setEbaySignInError('');
    try {
      const consentState = createEbayConsentState();
      const { data } = await authHttp.post<OAuthConsentLink>(
        CREATE_OAUTH_CONSENT_LINK_URL,
        { Platform: config.key, State: consentState }
      );
      if (hasBeenAbandoned()) return;
      const consentUrl = data?.consentUrl;
      if (!isEbaySignInUrl(consentUrl)) {
        stopOpeningEbay(EBAY_SIGN_IN_COULD_NOT_OPEN);
        return;
      }
      rememberEbayConsentState(consentState);
      if (readEbayConsentState() !== consentState) {
        stopOpeningEbay(BROWSER_BLOCKS_SITE_STORAGE);
        return;
      }
      window.location.assign(consentUrl);
    } catch (error) {
      if (hasBeenAbandoned()) return;
      setErrorResponseStatusCode(null);
      setResponseErrorMessages([]);
      stopOpeningEbay(whyTheEbaySignInCouldNotOpen(error));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      className='w-full sm:w-[440px]'
    >
      <form onSubmit={handleSubmit}>
        <div className='-mx-4 -mt-4 border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white px-6 pt-6 pb-5 sm:-mx-6 sm:-mt-6'>
          <div className='flex items-center gap-4'>
            <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-gray-200'>
              {config.logo && !logoError ? (
                <img
                  src={config.logo}
                  alt=''
                  onError={() => setLogoError(true)}
                  className='h-8 w-8 object-contain'
                />
              ) : (
                <span className='text-xl font-semibold text-primary'>
                  {config.name.charAt(0)}
                </span>
              )}
            </div>
            <div className='min-w-0 pr-8'>
              <p className='text-[11px] font-semibold uppercase tracking-wider text-primary'>
                {config.category}
              </p>
              <DialogTitle
                as='h2'
                className='text-lg font-semibold leading-6 text-gray-900'
              >
                {isReconnecting ? 'Reconnect' : 'Connect'} {config.name}
              </DialogTitle>
            </div>
          </div>
          <p className='mt-3 text-sm leading-relaxed text-gray-500'>
            {config.description}
          </p>
        </div>

        <div className='flex flex-col gap-4 pt-6'>
          {isEbaySignIn && (
            <div className='flex flex-col gap-3 text-sm leading-relaxed text-gray-600'>
              <p>
                Flox only reads your eBay listings and orders. eBay will also
                ask you to let Flox manage order fulfilment, so marking orders
                as shipped can be added later without connecting again.
              </p>
              <p>
                You&apos;ll be sent to eBay to sign in and approve, then brought
                straight back.
              </p>
            </div>
          )}
          {config.fields.map((field) =>
            field.type === 'warehouse' ? (
              <Select
                key={field.key}
                label={field.label}
                subText={warehousePickerHint(
                  field,
                  isLoadingWarehouses,
                  hasWarehouseLoadFailed,
                  warehouseOptions.length,
                  contractedMatchCount
                )}
                optional={field.optional}
                placeholder={
                  isLoadingWarehouses
                    ? 'Loading warehouses…'
                    : 'Select a warehouse'
                }
                options={warehouseOptions}
                selected={
                  warehouseOptions.find(
                    (option) => option.value === values[field.key]
                  ) ?? null
                }
                setSelected={(option) =>
                  setValues((prev) => ({
                    ...prev,
                    [field.key]: String(option.value),
                  }))
                }
              />
            ) : (
              <Input
                key={field.key}
                name={field.key}
                label={field.label}
                type={field.type ?? 'text'}
                placeholder={field.placeholder}
                helperText={field.helperText}
                optional={field.optional}
                value={values[field.key] ?? ''}
                autoComplete={
                  field.type === 'password' ? 'new-password' : 'off'
                }
                autoCapitalize='none'
                autoCorrect='off'
                spellCheck={false}
                onChange={(e) =>
                  setValues((prev) => ({
                    ...prev,
                    [field.key]: e.target.value,
                  }))
                }
              />
            )
          )}
        </div>

        {errorMessage && (
          <div
            role='alert'
            className='mt-4 flex items-start gap-2.5 rounded-lg bg-red-50 px-3 py-2.5 ring-1 ring-inset ring-red-100'
          >
            <ExclamationTriangleIcon
              aria-hidden='true'
              className='mt-0.5 h-4 w-4 shrink-0 text-red-500'
            />
            <p className='text-sm text-red-700'>{errorMessage}</p>
          </div>
        )}

        <div className='mt-5 flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2.5'>
          <LockClosedIcon
            aria-hidden='true'
            className='h-4 w-4 shrink-0 text-gray-400'
          />
          <span className='text-xs text-gray-500'>
            {isEbaySignIn
              ? "You sign in on eBay's own site, so Flox never sees your eBay password."
              : 'Your credentials are encrypted and stored securely.'}
          </span>
        </div>

        <div className='-mx-4 -mb-4 mt-6 flex justify-end gap-3 border-t border-gray-100 bg-gray-50/60 px-6 py-4 sm:-mx-6 sm:-mb-6'>
          <Button
            label='Cancel'
            intent='secondary'
            size='sm'
            onClick={() => setIsOpen(false)}
          />
          {isEbaySignIn ? (
            <Button
              label={isOpeningEbay ? 'Opening eBay…' : 'Continue to eBay'}
              intent='primary'
              size='sm'
              startIcon={isOpeningEbay ? <ButtonSpinner /> : undefined}
              disabled={isOpeningEbay}
              onClick={handleContinueToEbay}
            />
          ) : (
            <Button
              label={isSubmitting ? 'Connecting…' : 'Connect'}
              intent='primary'
              size='sm'
              type='submit'
              startIcon={isSubmitting ? <ButtonSpinner /> : undefined}
              disabled={!isComplete || isSubmitting}
            />
          )}
        </div>
      </form>
    </Modal>
  );
}
